import * as React from 'react';
import { message } from 'antd';
import { useSetAtom, useAtom, useAtomValue } from 'jotai';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import cloneDeep from 'lodash/cloneDeep';
import { getFirstAppliedFilter } from '@store/common/selectors';
import { applyFilter } from '@store/common/actions';
import { pushDrilldownFilter } from '@store/dimensionContainer/actions';
import { resetDrillDown } from '@store/filterContainer/actions';
import { updateRule, removeRule, resetFilter } from '@store/trends/base/actions';
import { needCheckDimensionSelector } from '@store/tempDashboard/selectors';
import { compareOnTrendsModeSelector } from '@store/trends/base/selectors';
import {
  allDimensionsListSelector,
  dimensionsListSelectorKeyValue,
} from '@store/common/dimensionList/selectors';
import { CURRENT_PRODUCT_MODE, PRODUCT_MODE_MAP } from '@utils/constants';
import { getValidFilterDimensionMeta } from '@utils';
import { getOrderedRulesWithName } from '@utils/filter';
import apptracker from '@utils/sensor/apptracker';
import * as EVENTS from '@src/utils/sensor/track';
import { GlobalFilterRefAtom } from '@src/atoms/page';
import { primaryFilterVisibilityAtom } from '@src/components/metrics-panel-hz/atoms';
import { FIXED_FILTERS, FIXED_APP_FILTERS } from '@src/components/filter-panel/constants';
import { FIlTER_CONTAINS_INAPPLICALBE_DIMENSION_ERROR } from '@src/components/summary-panel/FilterDropdown/FilterStatus';
import { TABLE_MODES } from '@components/dimension-panel-hz/atoms';
import * as apis from '@src/api/filters';
import FilterDialog, { type FilterDialogProps, type FilterDialogRef } from '../components/Dialog';
import { getDataSource, useCheckSavedFilterInValid } from '../components/utils';
import {
  enableFilterAtom,
  globalFilterRulesExpandedAtom,
  globalFilterAppendedAtomStatusAtom,
} from '../global-atoms';
import { useCreateFilter, useFilterMeta, useAdvancedFilterValue } from './hooks';

// Give a negative number to preset filters'`fid`, so that they can be distinguished from saved filters
const PRESET_FILTERS = (CURRENT_PRODUCT_MODE === 'APP' ? FIXED_APP_FILTERS : FIXED_FILTERS).map(
  (it, ix) => ({ ...it, fid: -1000 - ix }),
);

const filterDataSource = getDataSource();
const filterMetaEnabled = [PRODUCT_MODE_MAP.EI, PRODUCT_MODE_MAP.AI, PRODUCT_MODE_MAP.APP].includes(
  CURRENT_PRODUCT_MODE,
);

const appliedFilterSelector = createSelector(getFirstAppliedFilter, (filterValue) => {
  let fid: number | undefined = filterValue.fid;

  // Find out the PRESET FILTER, and give it a negative number as `fid`.
  // Inside the Filter Dialog component, it use `fid` to determine whether a filter rule can be saved.
  if (filterValue.fid === undefined && PRESET_FILTERS.some((it) => it.name === filterValue.name)) {
    fid = PRESET_FILTERS.find((it) => it.name === filterValue.name)?.fid;
  }

  return {
    name: filterValue.name,
    rule: filterValue.rule,
    fid,
  };
});

export const FilterDialogContainer = React.memo(() => {
  const filterDialogRef = React.useRef<FilterDialogRef>(null);
  const storeDispatch = useDispatch();
  const appliedFilter = useSelector(appliedFilterSelector);
  const allDimensions = useSelector<unknown, any>(allDimensionsListSelector);
  const dimensionLookup = useSelector<unknown, Record<string, string>>(
    dimensionsListSelectorKeyValue,
  );
  const needCheckDimension = useSelector(needCheckDimensionSelector);
  const compareMode = useSelector<never, boolean>(compareOnTrendsModeSelector);
  const checkSavedFilterInValid = useCheckSavedFilterInValid();
  const filterMeta = useFilterMeta(filterDataSource, {
    enabled: filterMetaEnabled,
    select: (data) => ({
      ...data,
      dimensions: data.dimensions.toSorted(({ name: a }, { name: b }) =>
        a === b ? 0 : a > b ? 1 : -1,
      ),
    }),
  });
  const enableFilter = useAtomValue(enableFilterAtom);

  const [advancedFilterValue, advancedFilterEnabled, setDimensionTableMode] =
    useAdvancedFilterValue();
  const setGlobalFilterRef = useSetAtom(GlobalFilterRefAtom);
  const [showPrimaryFilterSeries, setShowPrimaryFilterSeries] = useAtom(
    primaryFilterVisibilityAtom,
  );
  const [rulesExpanded, setRulesExpanded] = useAtom(globalFilterRulesExpandedAtom);
  const [appendedAtomStatus, setAppendedAtomStatus] = useAtom(globalFilterAppendedAtomStatusAtom);
  const filterMetadata = React.useMemo(() => {
    if (!filterMeta.data || !needCheckDimension) return filterMeta.data;

    const dimensions = filterMeta.data.dimensions || [];
    const validDimensions = getValidFilterDimensionMeta(dimensions, allDimensions);

    return {
      ...filterMeta.data,
      dimensions: validDimensions,
    };
  }, [filterMeta.data, needCheckDimension, allDimensions]);
  const { mutateAsync: filterSaveMutate } = useCreateFilter();

  const customTagMeta = filterMetadata?.dimensions.find((it) => it.name === 'Tag')?.fields[0];
  const customTagSearchUrl = customTagMeta?.urls.key;

  // Callbacks
  const handleChange = React.useCallback<NonNullable<FilterDialogProps['onChange']>>(
    (value) => {
      const { fid, name, rule } = value;
      const isSavedFilter = typeof fid === 'number' && fid > 0;
      // Remove the nagative number `fid` to keep the compatibility with old filter dialog
      const realFilterId = isSavedFilter ? fid : undefined;

      // saved filter including event dimension can't be selected under Session data scope
      if (isSavedFilter && checkSavedFilterInValid(value as any)) {
        message.warning(FIlTER_CONTAINS_INAPPLICALBE_DIMENSION_ERROR);
        return;
      }

      const orderedRules = getOrderedRulesWithName(cloneDeep(rule));

      const filterValue = {
        rule: orderedRules.rule,
        name: name || (orderedRules.rule.length ? orderedRules.name : undefined),
        fid: realFilterId,
      };

      storeDispatch(applyFilter(filterValue));
      storeDispatch(resetDrillDown());
      storeDispatch(pushDrilldownFilter(filterValue.name, filterValue.rule));

      // reset the advanced filter
      storeDispatch(resetFilter());
      setDimensionTableMode(TABLE_MODES.drilldown);
    },
    [storeDispatch, checkSavedFilterInValid, setDimensionTableMode],
  );
  const handleRecentFilterSearch = React.useCallback(async () => {
    const res = await apis.getFiltersBySearch({
      params: {
        sortBy: 'createdAt',
        sortOrder: 'desc',
        limit: 20,
      },
      ...apis.getV2AxiosConfig(),
    });
    return res.filters;
  }, []);
  const handleSavedFilterSearch = React.useCallback(async (keyword?: string) => {
    apptracker(EVENTS.FILTER_BUILDER_SAVED_FILTER_SEARCH, { keyword });
    const res = await apis.getFiltersBySearch({
      params: {
        searchText: keyword,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        limit: 200,
      },
      ...apis.getV2AxiosConfig(),
    });
    return res.filters;
  }, []);
  const handleCustomTagSearch = React.useCallback(
    async (keyword?: string) => {
      if (customTagSearchUrl && keyword) {
        const res = await apis.getAutoCompletion<{ text: string; value: string }[]>(
          customTagSearchUrl,
          keyword,
        );
        return res.data;
      }

      return [];
    },
    [customTagSearchUrl],
  );
  const handleDimensionValueSearch = React.useCallback((keyword?: string, apiUrl?: string) => {
    if (!apiUrl || !keyword) return [];

    return apis
      .getAutoCompletion<{ text: string; value: string }[]>(apiUrl, keyword)
      .then((res) => res.data);
  }, []);
  const handleFilterSave = React.useCallback<NonNullable<FilterDialogProps['onFilterSave']>>(
    async (filter) => {
      const res = await filterSaveMutate({
        filter,
        dataSource: filterDataSource,
      });

      return res.data.filter;
    },
    [filterSaveMutate],
  );
  const handleonAdvancedFilterAtomChange = React.useCallback<
    NonNullable<FilterDialogProps['onAdvancedFilterAtomChange']>
  >(
    (value, op) => {
      storeDispatch(updateRule(value, op));
    },
    [storeDispatch],
  );
  const handleonAdvancedFilterAtomRemove = React.useCallback<
    NonNullable<FilterDialogProps['onAdvancedFilterAtomRemove']>
  >(
    (value) => {
      storeDispatch(removeRule(value));
    },
    [storeDispatch],
  );
  const handleResetAdvancedFilter = React.useCallback(() => {
    storeDispatch(resetFilter());
  }, [storeDispatch]);
  const handleExitAdvancedFilter = React.useCallback(() => {
    storeDispatch(resetFilter());
    setDimensionTableMode(TABLE_MODES.drilldown);
  }, [setDimensionTableMode, storeDispatch]);

  React.useEffect(() => {
    setGlobalFilterRef(filterDialogRef);

    // Clean up, remove the reference to avoid memory leak.
    return () => {
      setGlobalFilterRef(undefined);
    };
  }, [setGlobalFilterRef]);

  // Control the inner status by the advanced filter value changes
  React.useEffect(() => {
    const innerValue = filterDialogRef.current?.getInnerFilterValue();
    filterDialogRef.current?.updateInnerStatus((current) => {
      if (advancedFilterValue?.length && current !== 'editing') {
        return 'editing';
      }

      if (!advancedFilterValue?.length && !innerValue?.rule.length && current === 'editing') {
        return 'idle';
      }

      return current;
    });
  }, [advancedFilterValue]);

  if (filterMetaEnabled && filterMeta.isLoading) return null;

  return (
    <div
      data-testid="filter-dialog"
      style={{ position: 'relative', zIndex: 50, wordBreak: 'initial', maxWidth: '100%' }}
    >
      <FilterDialog
        ref={filterDialogRef}
        defaultRulesExpanded={rulesExpanded}
        defaultAppendedAtomStatus={appendedAtomStatus}
        disabled={!enableFilter || !filterMetaEnabled}
        compareMode={compareMode}
        compareSeries={showPrimaryFilterSeries}
        meta={filterMetadata}
        dimensionLookup={dimensionLookup}
        presetFilters={PRESET_FILTERS}
        value={appliedFilter}
        advancedFilterEnabled={advancedFilterEnabled}
        advancedFilterValue={advancedFilterValue}
        onChange={handleChange}
        onCompareSeriesChange={setShowPrimaryFilterSeries}
        onRulesExpandChange={setRulesExpanded}
        onAppendedAtomStatusChange={setAppendedAtomStatus}
        onRecentFilterSearch={handleRecentFilterSearch}
        onSavedFilterSearch={handleSavedFilterSearch}
        onFilterSave={handleFilterSave}
        onCustomTagSearch={customTagSearchUrl ? handleCustomTagSearch : undefined}
        onDimensionValueSearch={handleDimensionValueSearch}
        onAdvancedFilterAtomChange={handleonAdvancedFilterAtomChange}
        onAdvancedFilterAtomRemove={handleonAdvancedFilterAtomRemove}
        onResetAdvancedFilter={handleResetAdvancedFilter}
        onExitAdvancedFilter={handleExitAdvancedFilter}
      />
    </div>
  );
});

FilterDialogContainer.displayName = 'FilterDialogContainer';
